import { ProductInProviderWarehouse } from 'src/typings';

export interface ProductSupplierOffer {
  id: string;
  warehouseId: number | null;
  warehouseName: string;
  cityId: number | null;
  cityName: string;
  quantity: number;
  price: number | null;
  availability: 'local' | 'other' | 'unavailable';
}

export interface ProductSupplierGroup {
  key: string;
  providerName: string;
  offers: ProductSupplierOffer[];
  bestOffer: ProductSupplierOffer;
}

function positiveId(value: unknown): number | null {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : null;
}

function availabilityRank(offer: ProductSupplierOffer): number {
  return offer.availability === 'local' ? 0 : offer.availability === 'other' ? 1 : 2;
}

function comparePrice(left: ProductSupplierOffer, right: ProductSupplierOffer): number {
  const leftPrice = left.price ?? Infinity;
  const rightPrice = right.price ?? Infinity;
  return leftPrice === rightPrice ? 0 : leftPrice < rightPrice ? -1 : 1;
}

function compareOffers(left: ProductSupplierOffer, right: ProductSupplierOffer): number {
  return availabilityRank(left) - availabilityRank(right)
    || comparePrice(left, right)
    || (left.warehouseId ?? Infinity) - (right.warehouseId ?? Infinity)
    || left.warehouseName.localeCompare(right.warehouseName, 'ru')
    || left.id.localeCompare(right.id);
}

/** Pick and rank the cheapest quote in each supplier's best availability tier. */
export function groupProductSupplierOffers(
  stock: readonly ProductInProviderWarehouse[] | undefined,
  managerCityId: number | null
): ProductSupplierGroup[] {
  const localCityId = positiveId(managerCityId);
  const groups = new Map<string, { key: string; providerName: string; offers: ProductSupplierOffer[] }>();

  (stock || []).forEach((row, index) => {
    const warehouse = row.providerWarehouse;
    const providerId = positiveId(warehouse?.provider?.id);
    const warehouseId = positiveId(warehouse?.id);
    // Unidentified providers must not combine unrelated warehouses into one supplier.
    const key = providerId ? `provider-${providerId}`
      : warehouseId ? `unknown-warehouse-${warehouseId}` : `unknown-offer-${index}`;
    let group = groups.get(key);
    if (!group) {
      group = { key, providerName: warehouse?.provider?.description || 'Не указан', offers: [] };
      groups.set(key, group);
    }

    const quantity = Number(row.quan);
    const price = Number(row.price);
    const cityId = positiveId(warehouse?.city?.id);
    group.offers.push({
      id: String(row.id ?? index),
      warehouseId,
      warehouseName: warehouse?.description || 'Склад не указан',
      cityId,
      cityName: warehouse?.city?.description || '—',
      quantity: Number.isFinite(quantity) ? quantity : 0,
      price: Number.isFinite(price) && price > 0 ? price : null,
      availability: Number.isFinite(quantity) && quantity > 0
        ? localCityId && cityId === localCityId ? 'local' : 'other'
        : 'unavailable'
    });
  });

  return Array.from(groups.values()).map((group) => {
    const offers = group.offers.sort(compareOffers);
    return { ...group, offers, bestOffer: offers[0] };
  }).sort((left, right) => availabilityRank(left.bestOffer) - availabilityRank(right.bestOffer)
    || comparePrice(left.bestOffer, right.bestOffer)
    || left.providerName.localeCompare(right.providerName, 'ru')
    || left.key.localeCompare(right.key));
}
